import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight, Database, Sparkles, TrendingUp, Library } from "lucide-react";
import { GlobalSearch } from "@/components/GlobalSearch";
import { CardDetailView } from "@/components/CardDetailView";
import { CardCarousel } from "@/components/CardCarousel";
import { api, type CardsListResp } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

export function Home() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const card = params.get("card");
  const [total, setTotal] = useState(0);

  // Compteur de cartes pour le hero (léger : 1 carte suffit à récupérer le total).
  useEffect(() => {
    let cancelled = false;
    api<CardsListResp>(`/api/cards?search=&limit=1&page=1`)
      .then((r) => !cancelled && setTotal(r.total ?? 0))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="space-y-6">
      {/* Hero masqué quand une carte est ouverte → moins d'encombrement (surtout mobile). */}
      {!card ? <Hero count={total} /> : null}

      {/* La recherche est le point d'entrée central de l'accueil, présente même
          carte ouverte (mais compacte). */}
      <div className="mx-auto max-w-2xl">
        <GlobalSearch variant="hero" />
        {!card ? (
          <p className="mt-2 text-center text-xs text-muted-fg">
            {t("home.search.hint") || "Type at least 3 characters, then press Enter to open a card."}
          </p>
        ) : null}
      </div>

      {/* Le détail s'affiche sous la barre — on reste visuellement sur l'accueil.
          Tant qu'aucune carte n'est ouverte, on montre le carrousel d'attente. */}
      {card ? <CardDetailView key={card} cardId={card} embedded /> : <CardCarousel />}
    </div>
  );
}

function Hero({ count }: { count: number }) {
  const { t } = useI18n();
  return (
    <div className="grid gap-4 md:grid-cols-3">
      <div className="md:col-span-2 rounded-lg border border-border bg-gradient-to-br from-surface to-surface-2 p-6 shadow-soft">
        <Badge>
          <Sparkles className="h-3 w-3" /> TCGPricer
        </Badge>
        <h1 className="text-2xl font-semibold tracking-tight md:text-3xl">
          {t("home.hero.title") || "Real-time TCG card pricing"}
        </h1>
        <p className="mt-2 max-w-xl text-sm text-muted-fg">
          {t("home.hero.subtitle") ||
            "Aggregated prices from Cardmarket, eBay sold listings, and on-chain marketplaces — searchable, versioned, source-verifiable."}
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-2 text-xs">
          <Link to="/cards" className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 font-medium text-primary-fg shadow-soft hover:bg-primary/90">
            <Library className="h-3.5 w-3.5" /> {t("home.hero.browse") || "Browse the catalog"} <ArrowUpRight className="h-3 w-3" />
          </Link>
          <Link to="/install" className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-3 py-1.5 text-fg hover:bg-surface-2">
            {t("home.hero.extension") || "Get the Chrome extension"} <ArrowUpRight className="h-3 w-3" />
          </Link>
        </div>
      </div>
      <div className="grid gap-3">
        <Stat icon={<Database className="h-4 w-4" />} label="Cards indexed" value={count.toLocaleString()} />
        <Stat icon={<TrendingUp className="h-4 w-4" />} label="Price sources" value="6+" hint="Cardmarket, eBay, RapidAPI…" />
      </div>
    </div>
  );
}

function Badge({ children }: { children: React.ReactNode }) {
  return (
    <span className="mb-3 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-medium text-primary">
      {children}
    </span>
  );
}

function Stat({ icon, label, value, hint }: { icon: React.ReactNode; label: string; value: string; hint?: string }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-border bg-surface p-4 shadow-soft">
      <div className="grid h-9 w-9 place-items-center rounded-md bg-surface-2 text-muted-fg">{icon}</div>
      <div className="flex-1">
        <div className="text-[11px] uppercase tracking-wide text-muted-fg">{label}</div>
        <div className="text-lg font-semibold tabular-nums">{value}</div>
        {hint ? <div className="text-[11px] text-muted-fg">{hint}</div> : null}
      </div>
    </div>
  );
}
